import { Injectable } from '@angular/core';
import { EquipmentService } from '../equipment/equipment.service';
import { RecordingInfo, RecordingIssue, StatsType } from '@sc/types';
import { UserService } from '../user/user.service';
import { StatsService } from '../stats/stats.service';
import { BehaviorSubject, firstValueFrom, skipWhile } from 'rxjs';
import { VideoOffService } from '../video-off/video-off.service';
import { MuteService } from '../mute/mute.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import {
  Storage,
  StorageReference,
  UploadTask,
  deleteObject,
  getStorage,
  listAll,
  ref,
  uploadBytesResumable,
} from '@angular/fire/storage';
import { getApp } from '@angular/fire/app';
import { environment } from '../../../environments/environment';
import { StorageService } from '../storage/storage.service';
import { deleteRecursive } from '@sc/firebase';
import { collection, deleteDoc, doc, Firestore } from '@angular/fire/firestore';
import { SessionsService } from '../sessions/sessions.service';

@Injectable({
  providedIn: 'root',
})
export class TestRecorderService {
  constraints$ = this.equipmentService.constraints$;
  TIMESLICE = 4 * 1000;

  recorder: MediaRecorder;
  startingRecorder = false;
  uploadTasks: UploadTask[] = [];
  stream: MediaStream;
  warnings: RecordingIssue[] = [];
  firstChunkTimeout: NodeJS.Timeout;
  firstChunkUploaded = false;
  chunkCounter = 0;

  recordingType: StatsType = StatsType.VIDEO;
  recordingInfo: RecordingInfo;
  recordingUpdate$ = new BehaviorSubject(null);

  videoOff = false;
  mute = false;

  deviceInfo = this.deviceDetectorService.getDeviceInfo();

  recordingBucket = this.storageService.testRecordingBucket;

  constructor(
    private equipmentService: EquipmentService,
    private userService: UserService,
    private statsService: StatsService,
    private videoOffService: VideoOffService,
    private muteService: MuteService,
    private firestore: Firestore,
    private deviceDetectorService: DeviceDetectorService,
    private storageService: StorageService,
    private sessionsService: SessionsService
  ) {
    this.setupVideoOff();
    this.setupMute();
  }

  async startRecording() {
    if (this.recorder?.state === 'recording' || this.startingRecorder) {
      return;
    }
    this.startingRecorder = true;

    try {
      await this.initialize();
      await this.setupRecorder();
      this.recorder.start(this.TIMESLICE);
    } catch (err) {
      this.warnings.push(RecordingIssue.RECORDING_START_FAILED);
      console.error(err);
      return;
    } finally {
      this.startingRecorder = false;
    }
  }

  async stopRecording() {
    while (this.startingRecorder) {
      await new Promise((res) => setTimeout(res, 100));
    }
    if (!this.recorder || this.recorder?.state === 'inactive') {
      return {
        recordingInfo: this.recordingInfo,
        warnings: this.warnings,
        update$: this.recordingUpdate$,
      };
    }

    this.recorder.stop();
    this.stream.getTracks().forEach((track) => track.stop());
    this.recordingInfo.stopped = true;

    return {
      recordingInfo: this.recordingInfo,
      warnings: this.warnings,
      update$: this.recordingUpdate$,
    };
  }

  async setupRecorder() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia(
        this.recordingType === StatsType.VIDEO
          ? { ...this.constraints$.value }
          : { audio: this.constraints$.value.audio }
      );
    } catch (e) {
      this.warnings.push(RecordingIssue.GET_USER_MEDIA_FAILED);
      return;
    }

    try {
      if (this.recordingType === StatsType.VIDEO) {
        this.recorder = this.equipmentService.getVideoRecorder(this.stream);
      } else {
        this.recorder = this.equipmentService.getAudioRecorder(this.stream);
      }
    } catch (e) {
      if (this.stream) this.stream.getTracks().forEach((track) => track.stop());
      this.warnings.push(RecordingIssue.GET_MEDIA_RECORDER_FAILED);
      return;
    }

    this.recordingInfo.stats[this.recordingType].trackSettings = {
      audio: this.recorder.stream.getAudioTracks()[0].getSettings(),
      video: this.recorder.stream.getVideoTracks()[0]?.getSettings() ?? null,
    };

    this.recorder.onstart = this.updateRecorderTrackEnabled.bind(this);
    this.recorder.ondataavailable = this.handleRecording.bind(this);
    this.recorder.onerror = this.handleError.bind(this);
  }

  updateRecorderTrackEnabled() {
    if (this.recordingType === StatsType.VIDEO) {
      this.recorder.stream.getVideoTracks()[0].enabled = !this.videoOff;
    }
    this.recorder.stream.getAudioTracks()[0].enabled = !this.mute;
  }

  handleError(e) {
    console.error(e);
    this.warnings.push(RecordingIssue.RECORDER_ERRORED);
  }

  async handleRecording(recording: { data: Blob }) {
    if (typeof recording.data === 'undefined' || recording.data.size === 0) {
      return;
    }
    const contentType = this.recorder.mimeType.includes('webm') ? 'video/webm' : 'video/mp4';
    this.chunkCounter = this.chunkCounter + 1;
    const chunkNum = this.chunkCounter;
    const blob = recording.data;
    if (chunkNum === 1) this.setFirstChunkTimeout();

    this.statsService.statsNewChunk(this.recordingInfo, undefined, this.recordingType, chunkNum, blob, true);

    await this.readyToUpload(chunkNum);
    // return if the recorder is null, can happen when the user cancels the test and starts recording again before all chunks are uploaded
    if (this.recorder === null) return;

    const chunkRef = ref(this.recordingBucket, `tests/${this.userService.activeUser$.value.uid}/chunks`);

    const fileRef = ref(chunkRef, `${chunkNum}.${contentType.split('/')[1]}`);
    const chunkUpload = uploadBytesResumable(fileRef, blob, { contentType });

    const t0 = performance.now();

    this.statsService.statsStartUpload(this.recordingInfo, this.recordingType, chunkNum, blob);

    chunkUpload.on(
      'state_changed',
      (snapshot) => {
        this.statsService.statsChunkProgress(
          this.recordingInfo,
          this.userService.activeUser$.value.uid,
          this.recordingType,
          chunkNum,
          snapshot,
          this.recordingUpdate$
        );
      },
      (e) => {
        if (e.code === 'storage/canceled') {
          console.log('user canceled upload');
          return;
        }
        console.error(e);
        this.warnings.push(RecordingIssue.CHUNK_UPLOAD_FAILED);
        this.statsService.statsChunkError(
          this.recordingInfo,
          this.userService.activeUser$.value.uid,
          this.recordingType,
          chunkNum,
          this.recordingUpdate$
        );
      },
      () => {
        this.statsService.statsChunkComplete(
          this.recordingInfo,
          this.userService.activeUser$.value.uid,
          this.recordingType,
          chunkNum,
          t0,
          this.recordingUpdate$,
          true
        );

        if (chunkNum === 1) {
          this.firstChunkUploaded = true;
        }
      }
    );

    this.uploadTasks.push(chunkUpload);
  }

  async readyToUpload(num: number) {
    await firstValueFrom(
      this.recordingUpdate$.pipe(
        skipWhile(() => {
          return this.recordingInfo.queue.has(num - 1);
        })
      )
    );
    return true;
  }

  setFirstChunkTimeout() {
    this.firstChunkTimeout = setTimeout(() => {
      if (!this.firstChunkUploaded) {
        this.warnings.push(RecordingIssue.FIRST_CHUNK_UPLOAD_DELAYED);
      }
    }, 60000);
  }

  async initialize() {
    this.warnings = [];

    await new Promise<void>((res) => {
      const interval = setTimeout(() => {
        res();
        this.warnings.push(RecordingIssue.FIRESTORE_CONNECTION_FAILED);
      }, 10000);

      (async () => {
        // delete previously written storage files
        const chunkRef = ref(this.recordingBucket, `tests/${this.userService.activeUser$.value.uid}`);
        await this.storageService.bucketRefDeleteRecursive(chunkRef);

        await deleteDoc(doc(collection(this.firestore, 'diagnostics'), this.userService.activeUser$.value.uid));

        clearTimeout(interval);
        res();
      })();
    });

    this.recordingType =
      this.constraints$.value.video && this.sessionsService.studioSession$.value.videoEnabled
        ? StatsType.VIDEO
        : StatsType.AUDIO;
    this.chunkCounter = 0;
    this.firstChunkUploaded = false;
    this.recorder = null;
    this.stream = null;
    this.recordingInfo = {
      filename: this.userService.activeUser$.value.uid,
      queue: new Map(),
      stats: { [this.recordingType]: { ...this.statsService.defaultRecordingStats } },
      lastBytesTransferred: 0,
    };
    this.recordingUpdate$.next(true);
    if (this.firstChunkTimeout) {
      clearTimeout(this.firstChunkTimeout);
      this.firstChunkTimeout = null;
    }

    this.uploadTasks.forEach((task) => task.cancel());
    this.uploadTasks = [];
  }

  setupVideoOff() {
    this.videoOffService.videoOff$.subscribe((videoOff) => {
      this.videoOff = videoOff;

      if (this.recorder?.state === 'recording') {
        this.updateRecorderTrackEnabled();
      }
    });
  }

  setupMute() {
    this.muteService.mute$.subscribe((mute) => {
      this.mute = mute;

      if (this.recorder?.state === 'recording') {
        this.updateRecorderTrackEnabled();
      }
    });
  }
}
